import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import {
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  Wrench,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Bell,
  Megaphone,
  Target,
  FileText,
  AlertCircle,
  Info,
  Gift,
  X,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stageLabels = {
  LEAD: "Lead",
  CONTACTADO: "Contactado",
  PROPUESTA: "Propuesta",
  NEGOCIACION: "Negociación",
  GANADA: "Ganada",
  PERDIDA: "Perdida",
};

const stageColors = {
  LEAD: "#94A3B8",
  CONTACTADO: "#03BEE4",
  PROPUESTA: "#F59E0B",
  NEGOCIACION: "#8B5CF6",
  GANADA: "#42E695",
  PERDIDA: "#EF4444",
};

const announcementIcons = {
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle2,
  promo: Gift,
};

const announcementColors = {
  info: "bg-[#03BEE4]/10 border-[#03BEE4]/30 text-[#03BEE4]",
  warning: "bg-orange-500/10 border-orange-500/30 text-orange-400",
  success: "bg-[#42E695]/10 border-[#42E695]/30 text-[#42E695]",
  promo: "bg-purple-500/10 border-purple-500/30 text-purple-400",
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [roleData, setRoleData] = useState(null);
  const [mrrHistory, setMrrHistory] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState([]);

  // Helpers for per-user, per-day announcement dismissal persistence.
  // Key: woc:announcement-dismissed:{userId}:{announcementId}:{YYYY-MM-DD}
  const todayKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const dismissalKey = (announcementId) =>
    `woc:announcement-dismissed:${user?.id || user?.email || "anon"}:${announcementId}:${todayKey()}`;

  // Determine effective role for dashboard display
  const hasElevated = user?.roles?.some(r => r === "SUPERADMIN" || r === "GESTION");
  const effectiveRole = hasElevated ? user?.active_role : null;
  const showComercial = hasElevated ? effectiveRole === "COMERCIAL" : user?.roles?.includes("COMERCIAL");
  const showTecnico = hasElevated ? effectiveRole === "TECNICO" : user?.roles?.includes("TECNICO");

  useEffect(() => {
    fetchAllData();
  }, [user?.active_role, user?.roles]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch general analytics
      const analyticsRes = await axios.get(`${API_URL}/api/analytics/dashboard`);
      setAnalytics(analyticsRes.data);
      
      // Fetch MRR history
      try {
        const mrrRes = await axios.get(`${API_URL}/api/analytics/mrr-history`);
        setMrrHistory(mrrRes.data);
      } catch (e) {
        console.log("MRR history not available");
      }
      
      // Fetch announcements
      try {
        const annRes = await axios.get(`${API_URL}/api/announcements`);
        setAnnouncements(annRes.data);
        // Rehydrate per-user, per-day dismissals from localStorage.
        const alreadyDismissed = (annRes.data || [])
          .filter((a) => {
            try { return localStorage.getItem(dismissalKey(a.id)) === "1"; }
            catch { return false; }
          })
          .map((a) => a.id);
        if (alreadyDismissed.length > 0) {
          setDismissedAnnouncements((prev) => Array.from(new Set([...prev, ...alreadyDismissed])));
        }
      } catch (e) {
        console.log("Announcements not available");
      }
      
      // Fetch role-specific data
      if (showComercial) {
        try {
          const roleRes = await axios.get(`${API_URL}/api/analytics/dashboard/comercial`);
          setRoleData(prev => ({ ...prev, comercial: roleRes.data }));
        } catch (e) {
          console.log("Comercial dashboard not available");
        }
      }
      if (showTecnico) {
        try {
          const roleRes = await axios.get(`${API_URL}/api/analytics/dashboard/tecnico`);
          setRoleData(prev => ({ ...prev, tecnico: roleRes.data }));
        } catch (e) {
          console.log("Tecnico dashboard not available");
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(value || 0);
  };

  const dismissAnnouncement = (id) => {
    try { localStorage.setItem(dismissalKey(id), "1"); } catch (e) { /* storage unavailable */ }
    setDismissedAnnouncements([...dismissedAnnouncements, id]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  const pipelineData = analytics?.pipeline_stages
    ? Object.entries(analytics.pipeline_stages)
        .filter(([stage]) => stage !== "GANADA" && stage !== "PERDIDA")
        .map(([stage, count]) => ({
          name: stageLabels[stage],
          value: count,
          color: stageColors[stage],
          mrr: analytics?.pipeline_mrr_by_stage?.[stage] || 0,
        }))
    : [];

  const totalPipeline = pipelineData.reduce((acc, item) => acc + item.value, 0);

  // Rich tooltip for the pipeline donut — stage, count, MRR total, % of active pipeline.
  const PipelineTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    const count = d.value || 0;
    const pct = totalPipeline > 0 ? Math.round((count / totalPipeline) * 100) : 0;
    const mrrStr = (d.mrr || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (
      <div
        className="rounded-md border px-3 py-2 text-sm shadow-lg"
        style={{ backgroundColor: "#0A0A35", borderColor: d.color || "#1E1E5F", minWidth: 180 }}
      >
        <div className="font-semibold mb-1" style={{ color: d.color }}>{d.name}</div>
        <div className="text-slate-200">{count} {count === 1 ? "oportunidad" : "oportunidades"}</div>
        <div className="text-slate-300">MRR estimado: <span className="text-[#42E695] font-semibold">{mrrStr} €</span></div>
        <div className="text-slate-400 text-xs mt-1">{pct}% del pipeline</div>
      </div>
    );
  };

  const visibleAnnouncements = announcements.filter(a => !dismissedAnnouncements.includes(a.id));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Announcements/Banners */}
      {visibleAnnouncements.length > 0 && (
        <div className="space-y-2">
          {visibleAnnouncements.map((announcement) => {
            const Icon = announcementIcons[announcement.type] || Info;
            const colorClass = announcementColors[announcement.type] || announcementColors.info;
            
            return (
              <div 
                key={announcement.id}
                className={`p-4 rounded-lg border ${colorClass} flex items-start justify-between gap-4`}
                data-testid={`announcement-${announcement.id}`}
              >
                <div className="flex items-start gap-3">
                  <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{announcement.title}</p>
                    <p className="text-sm opacity-80 mt-1">{announcement.message}</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => dismissAnnouncement(announcement.id)}
                  className="text-current hover:bg-white/10"
                  data-testid={`announcement-dismiss-${announcement.id}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white">
            Bienvenido, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-slate-400 mt-1">
            Resumen de operaciones de WiLAN
          </p>
        </div>
      </div>

      {/* Role-Specific Dashboard for COMERCIAL */}
      {showComercial && roleData?.comercial && (
        <div className="space-y-4">
          <h2 className="text-lg font-heading font-semibold text-[#03BEE4] flex items-center gap-2">
            <Target className="h-5 w-5" />
            Mi Rendimiento Comercial
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-[#42E695]/50">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Leads Convertidos</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-[#42E695]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{roleData.comercial.leads_converted}</div>
                <p className="text-xs text-slate-400 mt-1">Clientes ganados</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-[#03BEE4]/50">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Mis Oportunidades</CardTitle>
                <Target className="h-4 w-4 text-[#03BEE4]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{roleData.comercial.my_opportunities_count}</div>
                <p className="text-xs text-slate-400 mt-1">Activas en pipeline</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-[#42E695]/50">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Mi Cartera</CardTitle>
                <Users className="h-4 w-4 text-[#42E695]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{roleData.comercial.my_customers_count}</div>
                <p className="text-xs text-slate-400 mt-1">Clientes asignados</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-orange-500/50">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Tickets Abiertos</CardTitle>
                <AlertCircle className="h-4 w-4 text-orange-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  {roleData.comercial.open_incidents + roleData.comercial.open_requests}
                </div>
                <p className="text-xs text-slate-400 mt-1">Incidencias + Solicitudes</p>
              </CardContent>
            </Card>
          </div>
          
          {/* My Pipeline Stages */}
          <Card className="bg-[#0A0A35] border-[#1E1E5F]">
            <CardHeader>
              <CardTitle className="text-white font-heading text-base">Mi Pipeline por Etapa</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {Object.entries(roleData.comercial.pipeline_stages || {}).map(([stage, count]) => (
                  <div key={stage} className="text-center p-3 rounded-lg bg-[#1B1B4B]/50">
                    <div className="text-xl font-bold text-white">{count}</div>
                    <div className="text-xs text-slate-400">{stageLabels[stage]}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Role-Specific Dashboard for TECNICO */}
      {showTecnico && roleData?.tecnico && (
        <div className="space-y-4">
          <h2 className="text-lg font-heading font-semibold text-orange-400 flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Mi Trabajo Técnico
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-orange-500/50">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Provisiones Asignadas</CardTitle>
                <Wrench className="h-4 w-4 text-orange-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{roleData.tecnico.my_provisions_count}</div>
                <p className="text-xs text-slate-400 mt-1">Pendientes de instalación</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-yellow-500/50">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Tickets Técnicos</CardTitle>
                <FileText className="h-4 w-4 text-yellow-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{roleData.tecnico.my_tickets_count}</div>
                <p className="text-xs text-slate-400 mt-1">Por resolver</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-red-500/50">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Incidencias</CardTitle>
                <AlertTriangle className="h-4 w-4 text-red-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{roleData.tecnico.my_incidents_count}</div>
                <p className="text-xs text-slate-400 mt-1">Abiertas asignadas</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-[#42E695]/50">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Completadas Este Mes</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-[#42E695]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{roleData.tecnico.completed_this_month}</div>
                <p className="text-xs text-slate-400 mt-1">Provisiones finalizadas</p>
              </CardContent>
            </Card>
          </div>
          
          {/* My Provisions List */}
          {roleData.tecnico.my_provisions?.length > 0 && (
            <Card className="bg-[#0A0A35] border-[#1E1E5F]">
              <CardHeader>
                <CardTitle className="text-white font-heading text-base">Mis Provisiones Pendientes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {roleData.tecnico.my_provisions.slice(0, 5).map((prov) => (
                    <div key={prov.id} className="flex items-center justify-between p-3 rounded-lg bg-[#1B1B4B]/50">
                      <div>
                        <p className="text-white font-medium">{prov.provision_id}</p>
                        <p className="text-xs text-slate-400">{prov.customer_name} - {prov.location_name}</p>
                      </div>
                      <Badge className="bg-orange-500/20 text-orange-400">{prov.status}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* General KPIs (for SUPERADMIN/GESTION or as overview) */}
      {(user?.roles?.some(r => r === "SUPERADMIN" || r === "GESTION") || !roleData) && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* MRR Total */}
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-[#42E695]/50 transition-colors">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">
                  MRR Total
                </CardTitle>
                <DollarSign className="h-4 w-4 text-[#42E695]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  {formatCurrency(analytics?.total_mrr)}
                </div>
                <div className="flex items-center text-xs text-[#42E695] mt-1">
                  <ArrowUpRight className="h-3 w-3 mr-1" />
                  <span>+{analytics?.new_customers_this_month || 0} clientes este mes</span>
                </div>
              </CardContent>
            </Card>

            {/* Total Clientes */}
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-[#03BEE4]/50 transition-colors">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">
                  Clientes Activos
                </CardTitle>
                <Users className="h-4 w-4 text-[#03BEE4]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  {analytics?.total_customers || 0}
                </div>
                <div className="flex items-center text-xs text-[#42E695] mt-1">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  <span>+{analytics?.new_customers_this_month || 0} este mes</span>
                </div>
              </CardContent>
            </Card>

            {/* Net Growth */}
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-[#42E695]/50 transition-colors">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">
                  Crecimiento Neto
                </CardTitle>
                {(analytics?.net_growth || 0) >= 0 ? (
                  <TrendingUp className="h-4 w-4 text-[#42E695]" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                )}
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${
                  (analytics?.net_growth || 0) >= 0 ? "text-[#42E695]" : "text-red-500"
                }`}>
                  {(analytics?.net_growth || 0) >= 0 ? "+" : ""}{analytics?.net_growth || 0}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Nuevos - Bajas este mes
                </div>
              </CardContent>
            </Card>

            {/* Churn */}
            <Card className="bg-[#0A0A35] border-[#1E1E5F] hover:border-red-500/50 transition-colors">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">
                  Churn (Bajas)
                </CardTitle>
                <AlertTriangle className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  {analytics?.churned_customers || 0}
                </div>
                <div className="flex items-center text-xs text-red-400 mt-1">
                  <ArrowDownRight className="h-3 w-3 mr-1" />
                  <span>Clientes perdidos</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* MRR Evolution Chart */}
            <Card className="bg-[#0A0A35] border-[#1E1E5F] lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white font-heading">Evolución MRR</CardTitle>
                <CardDescription className="text-slate-400">
                  Últimos 6 meses
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={mrrHistory.length > 0 ? mrrHistory : [
                      { month: "Ene", mrr: analytics?.total_mrr * 0.8 || 0 },
                      { month: "Feb", mrr: analytics?.total_mrr * 0.85 || 0 },
                      { month: "Mar", mrr: analytics?.total_mrr * 0.9 || 0 },
                      { month: "Abr", mrr: analytics?.total_mrr * 0.95 || 0 },
                      { month: "May", mrr: analytics?.total_mrr * 0.98 || 0 },
                      { month: "Jun", mrr: analytics?.total_mrr || 0 },
                    ]}>
                      <defs>
                        <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#42E695" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#42E695" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1E1E5F" />
                      <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                      <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0A0A35",
                          border: "1px solid #1E1E5F",
                          borderRadius: "6px",
                        }}
                        labelStyle={{ color: "#fff" }}
                        formatter={(value) => [formatCurrency(value), "MRR"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="mrr"
                        stroke="#42E695"
                        strokeWidth={2}
                        fill="url(#mrrGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Pipeline Summary */}
            <Card className="bg-[#0A0A35] border-[#1E1E5F]">
              <CardHeader>
                <CardTitle className="text-white font-heading">Pipeline</CardTitle>
                <CardDescription className="text-slate-400">
                  {totalPipeline} oportunidades activas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[200px] flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pipelineData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pipelineData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<PipelineTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 mt-4">
                  {pipelineData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-slate-300">{item.name}</span>
                      </div>
                      <span className="text-white font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pending Provisions */}
            <Card className="bg-[#0A0A35] border-[#1E1E5F]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white font-heading">Provisiones Pendientes</CardTitle>
                    <CardDescription className="text-slate-400">
                      Altas en proceso
                    </CardDescription>
                  </div>
                  <Wrench className="h-5 w-5 text-[#F59E0B]" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="text-4xl font-bold text-[#F59E0B]">
                    {analytics?.pending_provisions || analytics?.pending_installations || 0}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Completadas</span>
                      <span className="text-white">
                        {analytics?.completed_provisions || 0} / {(analytics?.completed_provisions || 0) + (analytics?.pending_provisions || 0)}
                      </span>
                    </div>
                    <Progress 
                      value={
                        ((analytics?.completed_provisions || 0) / 
                        ((analytics?.completed_provisions || 0) + (analytics?.pending_provisions || 0) || 1)) * 100
                      } 
                      className="h-2 bg-[#1B1B4B]"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Won/Lost This Month */}
            <Card className="bg-[#0A0A35] border-[#1E1E5F]">
              <CardHeader>
                <CardTitle className="text-white font-heading">Conversiones del Mes</CardTitle>
                <CardDescription className="text-slate-400">
                  Oportunidades cerradas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 rounded-lg bg-[#42E695]/10 border border-[#42E695]/30">
                    <CheckCircle2 className="h-6 w-6 text-[#42E695] mx-auto mb-2" />
                    <div className="text-2xl font-bold text-[#42E695]">
                      {analytics?.pipeline_stages?.GANADA || 0}
                    </div>
                    <div className="text-xs text-slate-400">Ganadas</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                    <Clock className="h-6 w-6 text-red-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-red-500">
                      {analytics?.pipeline_stages?.PERDIDA || 0}
                    </div>
                    <div className="text-xs text-slate-400">Perdidas</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
